import { useSelector } from 'react-redux';

import { Anchor, Box, Page } from '@zillow/constellation-10';
import { PageFooter } from '@zillow/page-frame';

import StringConstants from '@common/constants/StringConstants';
import ImpersonationNav from '@common/components/ImpersonationNav';
import pathsManifest from '@renter-hub/pathsManifest';
import { onPageFrameLinkClick } from '@zg-rentals/datagrail';

import zillowRentalsLogo from '@assets/zillow-rentals.png';
import './index.scss';

interface PageTemplateProps {
  children: React.ReactNode;
}

const PageTemplate = ({ children }: PageTemplateProps) => {
  const { loggedIn, email, fromApp, impersonatorEmail, impersonatorUserToken } = useSelector((state) => ({
    // @ts-expect-error
    loggedIn: state.user.loggedIn,
    // @ts-expect-error
    email: state.user.email,
    // @ts-expect-error
    fromApp: state.app.cookieEnabledServices.fromApp,
    // @ts-expect-error
    impersonatorEmail: state.user.impersonatorEmail,
    // @ts-expect-error
    impersonatorUserToken: state.user.impersonatorUserToken,
  }));

  const isImpersonator = Boolean(impersonatorEmail && impersonatorUserToken);

  const handleStopImpersonatingClick = () => {
    if (typeof window !== 'undefined') {
      window.location.href = pathsManifest.auth.stopImpersonatingPage;
    }
  };

  const goToRenterHub = () => {
    window.location.href = pathsManifest.brandHomePage;
  };

  const goToSignInPage = () => {
    if (typeof window !== 'undefined') {
      const { pathname, search } = window.location;
      window.location = pathsManifest.signInPage({ pathname, search });
    }
  };

  const goToLogOutPage = () => {
    if (typeof window !== 'undefined') {
      window.location.href = pathsManifest.logOutPage();
    }
  };

  const isInMobileWebView = StringConstants.APP_SOURCES.includes(fromApp || '');

  return (
    <Page.Root css={{ background: 'white', fontFamily: 'ui' }}>
      {isImpersonator && (
        <ImpersonationNav emailAddress={email} handleStopImpersonatingClick={handleStopImpersonatingClick} />
      )}
      {!isInMobileWebView && (
        <Page.Content
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            borderBottom: '1px solid #d1d1d5',
            marginBlock: '0',
            paddingBlock: 'default',
          }}
        >
          <Box>
            <Anchor
              css={{ textDecoration: 'none', padding: 'default', float: 'left' }}
              onClick={goToRenterHub}
            >{`< ${StringConstants.BRANDS.FORMATTED.zillow} home`}</Anchor>
          </Box>
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <img className="zillowLogoImg" alt="Zillow" src={zillowRentalsLogo as unknown as string} />
          </Box>
          <Box>
            {email && loggedIn ? (
              <Anchor css={{ textDecoration: 'none', padding: 'default', float: 'right' }} onClick={goToLogOutPage}>
                Sign out
              </Anchor>
            ) : (
              <Anchor css={{ textDecoration: 'none', padding: 'default', float: 'right' }} onClick={goToSignInPage}>
                Sign in
              </Anchor>
            )}
          </Box>
        </Page.Content>
      )}
      <Page.Content>{children}</Page.Content>
      {!isInMobileWebView && (
        <Page.Content css={{ background: 'white' }}>
          <PageFooter onLinkClick={onPageFrameLinkClick} />
        </Page.Content>
      )}
    </Page.Root>
  );
};

export default PageTemplate;
